import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const paymentMethodDetailKeys = {
  all: ['paymentMethodDetails'],
  lists: () => [...paymentMethodDetailKeys.all, 'list'],
  details: () => [...paymentMethodDetailKeys.all, 'detail'],
  detail: (id) => [...paymentMethodDetailKeys.details(), id],
};

/**
 * Hook para obtener todos los detalles de métodos de pago
 */
export const usePaymentMethodDetails = () => {
  return useQuery({
    queryKey: paymentMethodDetailKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('payment_method_details');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

/**
 * Hook para obtener un detalle de método de pago específico
 * @param {string} id - UUID del detalle de método de pago
 * @param {Object} options - Opciones adicionales para la query
 */
export const usePaymentMethodDetail = (id, options = {}) => {
  return useQuery({
    queryKey: paymentMethodDetailKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`payment_method_details/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear un nuevo detalle de método de pago
 */
export const useCreatePaymentMethodDetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentMethodDetailData) => {
      const response = await apiClient.post('payment_method_details', paymentMethodDetailData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentMethodDetailKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating payment method detail:', error);
    },
  });
};

/**
 * Hook para actualizar un detalle de método de pago existente
 */
export const useUpdatePaymentMethodDetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`payment_method_details/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(paymentMethodDetailKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: paymentMethodDetailKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating payment method detail:', error);
    },
  });
};

/**
 * Hook para eliminar un detalle de método de pago
 */
export const useDeletePaymentMethodDetail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`payment_method_details/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: paymentMethodDetailKeys.lists() });
      queryClient.removeQueries({ queryKey: paymentMethodDetailKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting payment method detail:', error);
    },
  });
};
